import {DOCUMENT} from '@angular/common';
import {ErrorHandler, inject, Injectable, Injector} from '@angular/core';
import {LoggerTransferService} from '@scalified-monorepo/ng-shared/logger';
import {TransferredLogLevel} from '@scalified-monorepo/shared';
import {Case, DefaultCase, match} from 'pattern-matcher';
import {ErrorData, HttpError} from '../models/error-types';
import {ErrorService} from './error.service';

// User-facing error structure from backend
interface UserFacingErrorData {
  message: string;
  code?: string;
  description?: string;
  action?: {
    url: string;
    label: string;
  };
}

@Injectable({providedIn: 'root'})
export class GlobalErrorHandler extends ErrorHandler {
  lastError: {message: string; trace: string} | null = null;
  private injector = inject(Injector);

  constructor() {
    super();
    window.addEventListener('unhandledrejection', e => {
      this.handleError(e.reason);
      e.preventDefault();
    });
  }

  /**
   * Extracts error message from response header
   * @param error HTTP error response
   */
  errorFromHeader(error) {
    return error?.response?.headers.get('error-message');
  }

  /**
   * Processes and handles application errors
   * @param error The error to handle
   */
  override async handleError(error) {
    try {
      const errorService = this.injector.get(ErrorService);
      const document = this.injector.get(DOCUMENT);
      const errorFromHeader = this.errorFromHeader(error);

      const structuredError = this.extractStructuredError(error);

      const errorData: ErrorData = match(
        error,
        Case(HttpError, error => {
          // Use structured error data if available, otherwise fallback to legacy handling
          if (structuredError) {
            return new ErrorData({
              code: error.response.status,
              request: error.request,
              reasonPhrase: error.response.statusText,
              text: this.formatStructuredErrorMessage(structuredError),
            });
          }

          return new ErrorData({
            code: error.response.status,
            request: error.request,
            reasonPhrase: error.response.statusText,
            text: errorFromHeader || error.response.error,
          });
        }),
        DefaultCase(
          structuredError
            ? new ErrorData({text: this.formatStructuredErrorMessage(structuredError)})
            : new ErrorData({text: error.message || error})
        )
      );

      const {message, trace} = error.response
        ? {message: error.response.message, trace: error.response.error?.stackTrace}
        : {message: error.message, trace: error.stack};

      if (!document.location.host.includes('localhost')) {
        if (!this.lastError || this.lastError.message !== message || this.lastError.trace !== trace) {
          this.lastError = {message, trace};
          this.injector.get(LoggerTransferService).transferLog(TransferredLogLevel.Error, message, trace);
        }
      }

      errorService.emitError(errorData);
    } catch (e) {
      console.error(e);
      return;
    }
    super.handleError(error);
  }

  /**
   * Extracts structured error data from HTTP response if available
   */
  private extractStructuredError(error: {
    response?: {error?: UserFacingErrorData};
    message: string;
  }): UserFacingErrorData | null {
    // Check if the error response contains structured error data
    if (error?.response?.error && this.isUserFacingErrorData(error.response.error)) {
      return error.response.error;
    }

    // Check if the error itself is structured (for non-HTTP errors)
    if (this.isUserFacingErrorData(error)) {
      return error;
    }

    return null;
  }

  /**
   * Type guard to check if object is UserFacingErrorData
   */
  private isUserFacingErrorData(obj: {
    message: string;
    code?: string;
    description?: string;
    action?: {url: string; label: string};
  }): obj is UserFacingErrorData {
    return (
      obj &&
      typeof obj === 'object' &&
      typeof obj.message === 'string' &&
      (obj.code === undefined || typeof obj.code === 'string') &&
      (obj.description === undefined || typeof obj.description === 'string') &&
      (obj.action === undefined ||
        (typeof obj.action === 'object' && typeof obj.action.url === 'string' && typeof obj.action.label === 'string'))
    );
  }

  /**
   * Formats structured error for display
   */
  private formatStructuredErrorMessage(errorData: UserFacingErrorData): string {
    let message = errorData.message;

    if (errorData.code) {
      message += ` (${errorData.code})`;
    }

    if (errorData.description) {
      message += ` - ${errorData.description}`;
    }

    if (errorData.action) {
      message += ` [Action: ${errorData.action.label}]`;
    }

    return message;
  }
}
